import { useEffect, useMemo, useState } from "react";
import { X, ImagePlus, Loader2, Film } from "lucide-react";
import { Button } from "@/components/Button";
import { Field } from "@/components/Field";
import { cn } from "@/lib/cn";
import { createPost } from "@/lib/feedApi";

export interface ComposerProject {
  id: string;
  name: string;
  goals: { id: string; name: string }[];
}

interface Props {
  open: boolean;
  projects: ComposerProject[];
  onClose: () => void;
  onCreated: () => void;
}

const MAX_IMAGES = 10;

export function Composer({ open, projects, onClose, onCreated }: Props) {
  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState("");
  const [goalId, setGoalId] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [publish, setPublish] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goals = projects.find((p) => p.id === projectId)?.goals ?? [];

  const previews = useMemo(
    () => files.map((f) => ({ file: f, url: URL.createObjectURL(f), isVideo: f.type.startsWith("video/") })),
    [files],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  function reset() {
    setTitle("");
    setProjectId("");
    setGoalId("");
    setBody("");
    setFiles([]);
    setPublish(true);
    setError(null);
  }

  function addFiles(list: FileList | null) {
    if (!list) return;
    const incoming = Array.from(list);
    setFiles((cur) => {
      const images = [...cur.filter((f) => f.type.startsWith("image/")), ...incoming.filter((f) => f.type.startsWith("image/"))].slice(0, MAX_IMAGES);
      const video = incoming.find((f) => f.type.startsWith("video/")) ?? cur.find((f) => f.type.startsWith("video/"));
      return video ? [...images, video] : images;
    });
  }

  async function submit() {
    if (!body.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("story_post[title]", title);
      fd.append("story_post[body]", body);
      files.filter((f) => f.type.startsWith("image/")).slice(0, MAX_IMAGES).forEach((f) => fd.append("story_post[images][]", f));
      const video = files.find((f) => f.type.startsWith("video/"));
      if (video) fd.append("story_post[video]", video);
      if (projectId) fd.append("project_id", projectId);
      if (goalId) fd.append("goal_id", goalId);
      fd.append("publish_now", publish ? "true" : "false");

      await createPost(fd);
      reset();
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not publish your update.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto py-10" role="dialog" aria-modal="true">
      <button type="button" aria-label="Close" onClick={() => !submitting && onClose()} className="fixed inset-0 bg-overlay backdrop-blur-[2px]" />
      <div className="relative w-full max-w-xl rounded-[var(--radius-lg)] border border-border-subtle bg-surface shadow-[var(--shadow-4)]">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-3.5">
          <h2 className="text-sm font-semibold text-fg">Share an update</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={() => !submitting && onClose()}
            className="flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-fg-muted hover:bg-surface-hover hover:text-fg [&_svg]:size-4"
          >
            <X />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-5 py-4">
          <Field.Root>
            <Field.Label optional>Title</Field.Label>
            <Field.Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a title" />
          </Field.Root>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field.Root>
              <Field.Label optional>Project</Field.Label>
              <select
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value);
                  setGoalId("");
                }}
                className="h-9 w-full rounded-[var(--radius-md)] border border-border-default bg-surface px-3 text-sm text-fg hover:border-border-strong focus:outline-2 focus:outline-offset-2 focus:outline-border-focus"
              >
                <option value="">No project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Field.Root>

            <Field.Root>
              <Field.Label optional>Goal</Field.Label>
              <select
                value={goalId}
                onChange={(e) => setGoalId(e.target.value)}
                disabled={!projectId || goals.length === 0}
                className="h-9 w-full rounded-[var(--radius-md)] border border-border-default bg-surface px-3 text-sm text-fg hover:border-border-strong focus:outline-2 focus:outline-offset-2 focus:outline-border-focus disabled:cursor-not-allowed disabled:bg-muted disabled:text-fg-disabled"
              >
                <option value="">{projectId && goals.length ? "No specific goal" : "—"}</option>
                {goals.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </Field.Root>
          </div>

          <Field.Root>
            <Field.Label>Update</Field.Label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="What's happening on the ground?"
              className="w-full resize-y rounded-[var(--radius-md)] border border-border-default bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle hover:border-border-strong focus:outline-2 focus:outline-offset-2 focus:outline-border-focus"
            />
          </Field.Root>

          {previews.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {previews.map((p, i) => (
                <div key={i} className="relative aspect-square overflow-hidden rounded-[var(--radius-md)] border border-border-subtle bg-subtle">
                  {p.isVideo ? (
                    <div className="flex h-full flex-col items-center justify-center gap-1 p-1 text-center text-fg-muted [&_svg]:size-5">
                      <Film />
                      <span className="line-clamp-2 text-[10px]">{p.file.name}</span>
                    </div>
                  ) : (
                    <img src={p.url} alt="" className="h-full w-full object-cover" />
                  )}
                  <button
                    type="button"
                    aria-label="Remove"
                    onClick={() => setFiles((cur) => cur.filter((f) => f !== p.file))}
                    className="absolute right-1 top-1 flex size-5 items-center justify-center rounded-full bg-black/60 text-white [&_svg]:size-3"
                  >
                    <X />
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-danger-fg">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border-subtle px-5 py-3">
          <div className="flex items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-fg-muted hover:text-fg [&_svg]:size-4">
              <ImagePlus />
              <span className="hidden sm:inline">Photo / video</span>
              <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-fg-muted">
              <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="accent-[var(--brand-atlantic)]" />
              Publish now
            </label>
          </div>
          <Button intent="primary" onClick={submit} disabled={!body.trim() || submitting}>
            {submitting && <Loader2 className={cn("animate-spin")} />}
            {publish ? "Publish" : "Save draft"}
          </Button>
        </div>
      </div>
    </div>
  );
}
